import { useAuth0 } from '@auth0/auth0-react';

// Auth0 stamps the user's GLOBAL roles onto the ID token. Same claim keys as
// useIsFortiusAdmin — read both the HTTPS-namespaced and colon-style variants.
const ROLE_CLAIM_KEYS = [
    'https://fortius-app.com/roles',
    'fortius-app:roles',
] as const;

// Staff / privileged Auth0 role names. A user holding any of these carries
// elevated permissions that — because Auth0 roles/permissions are GLOBAL, not
// per-club — would apply to any club they're assigned to. Self-service
// "register with this club" must therefore never be offered to them: joining a
// club would immediately grant those powers over it. The backend enforces the
// same rule (the real security boundary); this guard only hides the affordance.
//
// Keep in sync with the backend `StaffRoles.All`
// (apps/api/src/clubbooking-model/Auth/StaffRoles.cs) and App.tsx
// `isPrivilegedUser`. Matched case-insensitively (e.g. `FrontOfHouse`).
const PRIVILEGED_ROLE_NAMES = new Set([
    'superadmin',
    'clubadmin',
    'clubowner',
    'frontofhouse',
    'staff',
    'guestsignup',
    'toursignup',
]);

/** Read the user's global role-name claim, tolerant of either claim shape. */
export const getUserRoles = (user: Record<string, unknown> | undefined): string[] => {
    if (!user) return [];
    for (const key of ROLE_CLAIM_KEYS) {
        const value = user[key];
        if (Array.isArray(value)) {
            return value.filter((r): r is string => typeof r === 'string');
        }
    }
    return [];
};

/** True if the roles list contains any staff / privileged role. */
export const hasPrivilegedRole = (user: Record<string, unknown> | undefined): boolean =>
    getUserRoles(user).some(r => PRIVILEGED_ROLE_NAMES.has(r.toLowerCase()));

/**
 * Whether the current user holds any staff / privileged role. Reads the Auth0
 * ID-token role claim, so it works even on the config-load error screen where
 * the tenant is unresolved and per-tenant permissions aren't available.
 */
export const useHasPrivilegedRole = (): boolean => {
    const { user } = useAuth0();
    return hasPrivilegedRole(user as Record<string, unknown> | undefined);
};
